import React, { useState, useMemo } from 'react';
import { Search, Download, Filter, MoreHorizontal, Plus, ChevronDown, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { Link } from 'react-router-dom';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@radix-ui/react-dropdown-menu';
import { useClassrooms } from './hooks/useClassrooms';
import { getClassroomColor } from './utils';

export default function Classrooms() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Fetch classrooms using the custom hook
  const { classrooms, loading, error, refetch } = useClassrooms();

  const handleClassSelection = (classId) => {
    setSelectedClasses((prev) => (prev.includes(classId) ? prev.filter((id) => id !== classId) : [...prev, classId]));
  };

  // Simple filtering with search
  const filteredClasses = useMemo(() => {
    return classrooms.filter((classroom) => {
      const matchesSearch = !searchTerm || 
        classroom.class_name?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || 
        (statusFilter === 'active' && classroom.status === 'active') ||
        (statusFilter === 'inactive' && classroom.status === 'inactive');
      
      return matchesSearch && matchesStatus;
    });
  }, [classrooms, searchTerm, statusFilter]);

  if (loading) {
    return (
      <div className="bg-background p-6 h-screen flex items-center justify-center">
        <div className="flex items-center gap-2">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span>Loading classrooms...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-background p-6 h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-destructive">Error loading classrooms: {error.message || 'Unknown error'}</p>
          <Button onClick={refetch} variant="outline">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background p-6 h-screen flex flex-col overflow-hidden">
      <div>
        <div className="md:flex space-y-4 items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-foreground">All Classes</h1>
          <div className="flex items-center gap-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2">
                  Bulk upload
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem>Upload CSV</DropdownMenuItem>
                <DropdownMenuItem>Upload Excel</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button className="gap-2" asChild>
              <Link to="/admin/classes/create" className="flex items-center">
                <Plus className="h-4 w-4" />
                Create class
              </Link>
            </Button>
          </div>
        </div>

        <div className="md:flex space-y-4 items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search for a class"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-64"
              />
            </div>
            <span className="text-sm text-muted-foreground">{filteredClasses.length} classes</span>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm">
              <Download className="h-4 w-4" />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  Status: {statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => setStatusFilter('all')}>All</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('active')}>Active</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('inactive')}>Inactive</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Filter className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem>All</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <div className="flex rounded-t-md bg-muted/40 hover:bg-muted/50 shrink-0 border border-b-0">
        <div className="px-2 py-3 w-10 text-left text-sm font-medium"></div>
        <div className="px-2 py-3 w-2/5 text-left text-sm font-medium">Class name</div>
        <div className="px-2 py-3 w-1/4 text-left text-sm font-medium">Subject</div>
        <div className="px-2 py-3 w-1/6 text-left text-sm font-medium">Status</div>
        <div className="px-2 py-3 w-1/6 text-left text-sm font-medium">Grade</div>
        <div className="px-2 py-3 w-16 text-left text-sm font-medium"></div>
      </div>

      <ScrollArea className="relative rounded-b-md flex-grow  border">
        <Table>
          <TableBody>
            {filteredClasses.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">
                  <div className="space-y-4">
                    <p className="text-muted-foreground">No classrooms found</p>
                    <Button asChild>
                      <Link to="/admin/classes/create" className="flex items-center gap-2">
                        <Plus className="h-4 w-4" />
                        Create your first class
                      </Link>
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              filteredClasses.map((cls, idx) => (
              <TableRow
                key={cls.id || cls._id}
                className={`hover:bg-muted/50 transition-colors ${idx % 2 !== 0 ? 'bg-muted/20' : ''}`}>
                <TableCell className="w-16">
                  <Checkbox
                    className="rounded"
                    checked={selectedClasses.includes(cls.id || cls._id)}
                    onCheckedChange={() => handleClassSelection(cls.id || cls._id)}
                  />
                </TableCell>
                <TableCell className="w-2/5 font-medium">
                  <div className="flex items-center gap-3">
                    <div className={`px-2 py-1 rounded text-xs font-medium ${getClassroomColor(idx)}`}>
                      {cls.class_name?.charAt(0).toUpperCase() || 'C'}
                    </div>
                    <span>{cls.class_name}</span>
                  </div>
                </TableCell>
                <TableCell className="w-1/4 text-muted-foreground">
                  {cls.subject || 'No subject'}
                </TableCell>
                <TableCell className="w-1/6">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    cls.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {cls.status}
                  </span>
                </TableCell>
                <TableCell className="w-1/6">{cls.grade_id || 'N/A'}</TableCell>
                <TableCell className="w-16">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem>Edit class</DropdownMenuItem>
                      <Separator className="border my-1" />
                      <DropdownMenuItem className="text-red-500">
                        Delete class
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            )))}
          </TableBody>
        </Table>
      </ScrollArea>
    </div>
  );
}
